import type { SocialPlatform } from '@mg/shared';
import type { AttachedAsset } from '../composer/blocks';

/**
 * The post a Generate media or Generate design window was opened from.
 *
 * Read from the editor as it is NOW, not from the saved post: someone who has just typed their copy
 * expects the prompt to be drafted from what they typed, and a new post has no saved version at all.
 */
export interface PostContext {
  /** The post's text, its text boxes joined in order. */
  text: string;
  /** Where it will be published, for the band's label and the character count. */
  platform: SocialPlatform | null;
  /** The account it will be published from, when one is chosen. */
  accountLabel?: string | null;
  /** The media already on the post, for the band's thumbnails. */
  media: { id: string; url: string; mimeType: string | null }[];
}

/**
 * Where a finished image goes when someone chooses "Save and return to post".
 *
 * Given only by the post editor, which has a post to put it in. A window opened from anywhere else
 * leaves it out and offers no "Save and return to post". `note` is something the person should see
 * in the post once they are back there, such as a design's PNG copy using a stand-in font.
 */
export type SaveToPost = (asset: AttachedAsset, note?: string) => void;

/** The longest line put on a design. Past this a line is a paragraph, not a headline. */
const MAX_LINE_CHARS = 120;

/**
 * "Generate text": the post's first two lines, for a design's Text and Second line.
 *
 * Lines rather than a summary, because the opening of a post is almost always its claim, and the
 * person can see exactly where the words came from. A first line that runs on is cut to its first
 * sentence; a post written as one paragraph gives its first two sentences.
 */
export function firstTwoLines(text: string): { text: string; subtext: string } {
  const lines = text
    .split(/\r?\n/)
    // A line of hashtags is never a headline: dropped before the markers are stripped, which would
    // otherwise take its leading # and leave "RevOps #Finance".
    .filter((line) => !/^#\w/.test(line.trim()))
    .map((line) => line.replace(/^[#>\-*•\s]+/, '').trim())
    .filter((line) => line.length > 0);
  const sentences = (line: string) => line.match(/[^.!?]+[.!?]+(?=\s|$)|[^.!?]+$/g)?.map((s) => s.trim()) ?? [];

  let [first = '', second = ''] = lines;
  if (lines.length === 1 || first.length > MAX_LINE_CHARS) {
    const [a = first, b = ''] = sentences(first);
    first = a;
    // A first line cut to a sentence keeps its own second sentence only when there is no second line.
    if (lines.length === 1) second = b;
  }
  if (second.length > MAX_LINE_CHARS) second = sentences(second)[0] ?? second;
  return { text: clip(first), subtext: clip(second) };
}

function clip(line: string): string {
  if (line.length <= MAX_LINE_CHARS) return line;
  const cut = line.slice(0, MAX_LINE_CHARS);
  const space = cut.lastIndexOf(' ');
  return `${(space > MAX_LINE_CHARS / 2 ? cut.slice(0, space) : cut).trimEnd()}…`;
}
